/**
 * Personal Avatar Card — lets a community member upload a selfie and generate
 * a stylized Hoyo Zero avatar via Gemini Nano Banana. The result waits for
 * admin approval before becoming the user's public avatar.
 *
 * States: none | pending | approved | rejected
 * Max 2 generation attempts. Resets to 0 if Joan rejects.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const COPY = {
  ca: {
    title: 'El teu avatar amb IA',
    subtitleNone: 'Penja una selfie i et generem un retrat exclusiu Hoyo Zero (polo verd + fons geomètric).',
    subtitlePending: 'El teu avatar està generat i pendent d\'aprovació per Joan.',
    subtitleApproved: 'El teu avatar personal ja es mostra a la teva comunitat.',
    cta: 'Crea el meu avatar',
    ctaRetry: 'Tornar a intentar-ho',
    pickSelfie: 'Tria una selfie',
    selfieHint: 'Frontal · mig pla · bona llum. JPG o PNG, fins a 8 MB.',
    generate: 'Generar avatar',
    generating: 'Generant amb IA…',
    successPending: 'Avatar enviat! Joan el revisarà aviat.',
    cancel: 'Cancel·lar',
    attempts: (n) => `Et queden ${n} intent${n === 1 ? '' : 's'}.`,
    noAttempts: 'Has esgotat els intents. Demana-li a Joan que en restableixi.',
    pendingBadge: 'Pendent d\'aprovació',
    approvedBadge: 'Aprovat',
    waitMsg: 'Pots tancar la pestanya. T\'avisarem quan estigui aprovat.',
  },
  es: {
    title: 'Tu avatar con IA',
    subtitleNone: 'Sube una selfie y te generamos un retrato exclusivo Hoyo Zero (polo verde + fondo geométrico).',
    subtitlePending: 'Tu avatar está generado y pendiente de aprobación por Joan.',
    subtitleApproved: 'Tu avatar personal ya se muestra en tu comunidad.',
    cta: 'Crear mi avatar',
    ctaRetry: 'Intentarlo de nuevo',
    pickSelfie: 'Elige una selfie',
    selfieHint: 'Frontal · medio plano · buena luz. JPG o PNG, hasta 8 MB.',
    generate: 'Generar avatar',
    generating: 'Generando con IA…',
    successPending: '¡Avatar enviado! Joan lo revisará pronto.',
    cancel: 'Cancelar',
    attempts: (n) => `Te quedan ${n} intento${n === 1 ? '' : 's'}.`,
    noAttempts: 'Has agotado los intentos. Pídele a Joan que los reinicie.',
    pendingBadge: 'Pendiente de aprobación',
    approvedBadge: 'Aprobado',
    waitMsg: 'Puedes cerrar la pestaña. Te avisaremos cuando esté aprobado.',
  },
  en: {
    title: 'Your AI avatar',
    subtitleNone: 'Upload a selfie and we\'ll craft an exclusive Hoyo Zero portrait (green polo + geometric backdrop).',
    subtitlePending: 'Your avatar is generated and waiting for Joan\'s approval.',
    subtitleApproved: 'Your personal avatar is live in your community.',
    cta: 'Create my avatar',
    ctaRetry: 'Try again',
    pickSelfie: 'Pick a selfie',
    selfieHint: 'Front-facing · half body · good lighting. JPG or PNG, up to 8 MB.',
    generate: 'Generate avatar',
    generating: 'Generating with AI…',
    successPending: 'Avatar submitted! Joan will review it soon.',
    cancel: 'Cancel',
    attempts: (n) => `You have ${n} attempt${n === 1 ? '' : 's'} left.`,
    noAttempts: 'You\'re out of attempts. Ask Joan to reset them.',
    pendingBadge: 'Awaiting approval',
    approvedBadge: 'Approved',
    waitMsg: 'You can close the tab. We\'ll let you know once approved.',
  },
  fr: {
    title: 'Ton avatar IA',
    subtitleNone: 'Charge un selfie et nous créons un portrait Hoyo Zero exclusif (polo vert + fond géométrique).',
    subtitlePending: 'Ton avatar est généré et en attente d\'approbation de Joan.',
    subtitleApproved: 'Ton avatar personnel est visible dans ta communauté.',
    cta: 'Créer mon avatar',
    ctaRetry: 'Réessayer',
    pickSelfie: 'Choisis un selfie',
    selfieHint: 'Vue de face · mi-plan · bonne lumière. JPG ou PNG, jusqu\'à 8 Mo.',
    generate: 'Générer l\'avatar',
    generating: 'Génération IA…',
    successPending: 'Avatar envoyé ! Joan le validera bientôt.',
    cancel: 'Annuler',
    attempts: (n) => `Il te reste ${n} essai${n === 1 ? '' : 's'}.`,
    noAttempts: 'Tu n\'as plus d\'essais. Demande à Joan de les réinitialiser.',
    pendingBadge: 'En attente',
    approvedBadge: 'Approuvé',
    waitMsg: 'Tu peux fermer l\'onglet. On te préviendra une fois validé.',
  },
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('read-failed'));
    reader.readAsDataURL(file);
  });

export const PersonalAvatarCard = ({ community, getToken, lang = 'ca', onApproved }) => {
  const copy = COPY[(lang || 'ca').split('-')[0]] || COPY.ca;
  const [data, setData] = useState(null); // {status, attempts_left, pending_url, personal_avatar_url}
  const [open, setOpen] = useState(false);
  const [selfie, setSelfie] = useState(null); // {file, dataUrl}
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);
  const prevStatusRef = useRef(null);
  const [imgBroken, setImgBroken] = useState(false);

  const reload = async () => {
    try {
      const token = getToken();
      if (!token) return;
      const r = await axios.get(`${API_URL}/api/${community}/avatar/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // If we just transitioned to approved, refresh the parent member object so
      // the big dashboard avatar updates immediately.
      if (
        prevStatusRef.current &&
        prevStatusRef.current !== 'approved' &&
        r.data?.status === 'approved'
      ) {
        if (typeof onApproved === 'function') onApproved();
      }
      prevStatusRef.current = r.data?.status;
      setData(r.data);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    reload();
    // light polling while pending so the user sees the approval flip
    const id = setInterval(() => {
      if (data?.status === 'pending') reload();
    }, 15000);
    return () => clearInterval(id);
  }, [community, data?.status]);

  // Reset broken-image flag whenever the candidate URL changes
  // (e.g. after a new generation or admin approval).
  useEffect(() => {
    setImgBroken(false);
  }, [data?.pending_url, data?.personal_avatar_url]);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error('Imatge massa gran (max 8 MB)');
      return;
    }
    const dataUrl = await fileToBase64(f);
    setSelfie({ file: f, dataUrl });
  };

  const submit = async () => {
    if (!selfie) return;
    setSubmitting(true);
    try {
      const token = getToken();
      const r = await axios.post(
        `${API_URL}/api/${community}/avatar/generate`,
        { selfie_base64: selfie.dataUrl },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 180000 },
      );
      toast.success(copy.successPending);
      setOpen(false);
      setSelfie(null);
      setData((d) => ({
        ...(d || {}),
        status: 'pending',
        pending_url: r.data.pending_url,
        attempts_left: r.data.attempts_left,
        attempts: (d?.attempts || 0) + 1,
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No s\'ha pogut generar');
    } finally {
      setSubmitting(false);
    }
  };

  if (!data) return null;

  const status = data.status;
  const attemptsLeft = data.attempts_left ?? 0;
  const previewUrl = data.pending_url || data.personal_avatar_url;

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.35 }}
      className="mt-8 rounded-2xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#083B1F]/60 to-[#0E4D2A]/30 backdrop-blur-sm p-5 md:p-6"
      data-testid="personal-avatar-card"
    >
      <div className="flex items-start gap-5">
        <div className="relative flex-shrink-0">
          {previewUrl && !imgBroken ? (
            <img
              src={previewUrl}
              alt="Personal avatar"
              onError={() => setImgBroken(true)}
              className="w-20 h-20 md:w-24 md:h-24 rounded-2xl border border-[#D4AF37]/40 object-cover"
              data-testid="personal-avatar-preview"
            />
          ) : (
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl border border-dashed border-[#D4AF37]/40 flex items-center justify-center text-3xl">
              🪞
            </div>
          )}
          {status === 'pending' && (
            <span className="absolute -bottom-2 -right-2 bg-[#D4AF37] text-[#083B1F] text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-0.5 rounded-full">
              {copy.pendingBadge}
            </span>
          )}
          {status === 'approved' && (
            <span className="absolute -bottom-2 -right-2 bg-[#A8D5BA] text-[#083B1F] text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-0.5 rounded-full">
              ✓ {copy.approvedBadge}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-xl md:text-2xl tracking-tight">{copy.title}</h3>
          <p className="mt-1 font-sans font-light text-[#A8B4A6] text-sm">
            {status === 'pending'
              ? copy.subtitlePending
              : status === 'approved'
                ? copy.subtitleApproved
                : copy.subtitleNone}
          </p>
          {status === 'none' && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              {attemptsLeft > 0 ? (
                <button
                  type="button"
                  onClick={() => setOpen(true)}
                  data-testid="personal-avatar-cta"
                  className="text-[#D4AF37] hover:text-[#E5C158] font-sans tracking-[0.18em] uppercase text-xs border border-[#D4AF37]/40 hover:border-[#D4AF37] px-4 py-2 rounded-full transition-all"
                >
                  → {copy.cta}
                </button>
              ) : (
                <span className="text-[#A8B4A6] text-xs italic">{copy.noAttempts}</span>
              )}
              {attemptsLeft > 0 && (
                <span className="text-[10px] tracking-[0.18em] uppercase text-[#A8B4A6]">{copy.attempts(attemptsLeft)}</span>
              )}
            </div>
          )}
          {status === 'pending' && (
            <p className="mt-3 text-[10px] tracking-[0.18em] uppercase text-[#A8B4A6] italic">{copy.waitMsg}</p>
          )}
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => !submitting && setOpen(false)}
            data-testid="personal-avatar-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#083B1F] border border-[#D4AF37]/30 rounded-2xl p-6 md:p-8 max-w-md w-full text-[#F2E8D5]"
            >
              <h4 className="font-serif text-2xl tracking-tight">{copy.title}</h4>
              <p className="mt-2 text-sm text-[#A8B4A6]">{copy.selfieHint}</p>

              <div className="mt-5">
                {selfie ? (
                  <div className="flex items-center gap-4">
                    <img src={selfie.dataUrl} alt="selfie" className="w-24 h-24 rounded-xl object-cover border border-[#D4AF37]/30" />
                    <button
                      type="button"
                      onClick={() => { setSelfie(null); if (fileRef.current) fileRef.current.value = ''; }}
                      className="text-xs tracking-[0.18em] uppercase text-[#A8B4A6] hover:text-[#D4AF37]"
                    >
                      {copy.pickSelfie} →
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    data-testid="personal-avatar-pick"
                    className="w-full border border-dashed border-[#D4AF37]/40 hover:border-[#D4AF37] rounded-xl py-8 text-[#D4AF37] text-sm tracking-[0.18em] uppercase"
                  >
                    📷 {copy.pickSelfie}
                  </button>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  capture="user"
                  className="hidden"
                  onChange={onPickFile}
                  data-testid="personal-avatar-file-input"
                />
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={submitting}
                  className="text-[#A8B4A6] hover:text-[#F2E8D5] text-xs tracking-[0.18em] uppercase px-3 py-2"
                >
                  {copy.cancel}
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={!selfie || submitting}
                  data-testid="personal-avatar-submit"
                  className="bg-[#D4AF37] hover:bg-[#E5C158] disabled:opacity-40 text-[#083B1F] font-sans tracking-[0.18em] uppercase text-xs px-5 py-2.5 rounded-full transition-all"
                >
                  {submitting ? copy.generating : `✨ ${copy.generate}`}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default PersonalAvatarCard;
